import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { AUTHOR } from '../content/author';

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-canvas py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-medium text-fg mb-4">
            About Vegetablah.com
          </h1>
          <p className="text-xl text-fg-2 max-w-2xl mx-auto">
            Empowering you to achieve your health goals with AI-powered guidance, comprehensive tracking from fitness devices and manual inputs, and insightful analysis.
          </p>
        </div>

        {/* Mission Section */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-2xl text-fg">Our Mission</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-fg-2 leading-relaxed">
              At Vegetablah.com, we believe that understanding your health patterns is the first step toward making meaningful improvements. 
              Our platform provides you with the tools and insights needed to connect your fitness devices, manually input health data, track your food intake, monitor your health metrics, 
              set personalized health goals, and use AI and machine learning to receive tailored advice on making progress toward those goals.
            </p>
          </CardContent>
        </Card>

        {/* Features Overview */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-2xl text-fg">What We Offer</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-medium text-fg mb-2">Food Logging</h3>
                <p className="text-fg-2">
                  Track your daily food intake with detailed nutritional information and portion sizes.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-medium text-fg mb-2">Health Monitoring</h3>
                <p className="text-fg-2">
                  Record and monitor various health metrics including weight, mood, energy levels, and more.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-medium text-fg mb-2">Fitness Device Integration</h3>
                <p className="text-fg-2">
                  Connect your fitness devices to automatically import and track health data seamlessly.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-medium text-fg mb-2">AI-Powered Goal Guidance</h3>
                <p className="text-fg-2">
                  Set personalized health goals and receive AI-driven advice based on your data inputs and outputs to make progress toward achieving them.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-medium text-fg mb-2">Correlation Analysis</h3>
                <p className="text-fg-2">
                  Discover patterns and correlations between your food choices and health outcomes.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-medium text-fg mb-2">Data Visualization</h3>
                <p className="text-fg-2">
                  Visualize your health journey with comprehensive charts and trend analysis.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Technology Section */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-2xl text-fg">Technology & Privacy</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <p className="text-fg-2 leading-relaxed">
                Vegetablah.com is built with modern web technologies, including AI and machine learning integrations, to ensure a fast, secure, and reliable experience. 
                We prioritize your privacy and data security, implementing industry-standard encryption and security measures 
                to protect your personal health information.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
                <div className="text-center p-4 bg-elev rounded-lg">
                  <h4 className="font-medium text-fg mb-2">Secure</h4>
                  <p className="text-sm text-fg-2">End-to-end encryption and secure data storage</p>
                </div>
                <div className="text-center p-4 bg-elev rounded-lg">
                  <h4 className="font-medium text-fg mb-2">Private</h4>
                  <p className="text-sm text-fg-2">Your data stays private and is never shared</p>
                </div>
                <div className="text-center p-4 bg-elev rounded-lg">
                  <h4 className="font-medium text-fg mb-2">Reliable</h4>
                  <p className="text-sm text-fg-2">Built for performance and reliability</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Author section. Articles link their byline to /about#author, so this
            is the page that has to answer "who is telling me this?" — the
            visible twin of the Person schema the server injects
            (backend/services/seo_render.py). It replaced a claim about a
            "passionate team of health enthusiasts, developers, and data
 scientists", which was neither true nor a trust signal. */}
        <Card className="mb-8" id="author">
          <CardHeader>
            <CardTitle className="text-2xl text-fg">
 Who's behind Vegetablah
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <p className="text-fg-2 leading-relaxed">
                <span className="font-medium text-fg">
                  {AUTHOR.name}
                </span>{' '}
                — {AUTHOR.role.toLowerCase()}. Vegetablah is built and run by one
                person: a software developer who wanted better answers from his own
                health data than any app was willing to give. He writes the platform
                end to end — the device integrations, the statistics and forecasting
                engines, and the app itself — and he's also its heaviest user. Every
                feature ships only after being tested against years of his own
                wearable, nutrition, and lab data.
              </p>
              <p className="text-fg-2 leading-relaxed">
 That's the standard behind everything here: numbers come from math you
                can check, forecasts get graded against what actually happened, and the
                app stays honest about what it doesn't know.
              </p>
              <div className="flex flex-wrap gap-4">
                <a
                  href={AUTHOR.github}
                  rel="me noopener noreferrer"
                  target="_blank"
                  className="text-sm font-medium text-fg-2 underline"
                >
                  GitHub
                </a>
                <a
                  href={AUTHOR.youtube}
                  rel="me noopener noreferrer"
                  target="_blank"
                  className="text-sm font-medium text-fg-2 underline"
                >
                  YouTube
                </a>
              </div>
              <p className="text-sm text-fg-3">
                Vegetablah offers general wellness information, not medical advice.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Contact CTA */}
        <div className="text-center">
          <Card>
            <CardContent className="py-8">
              <h3 className="text-xl font-medium text-fg mb-4">
                Have Questions or Feedback?
              </h3>
              <p className="text-fg-2 mb-6">
 We'd love to hear from you! Whether you have questions about using Vegetablah.com 
                or suggestions for new features, we're here to help.
              </p>
              <a 
                href="/contact" 
                className="inline-block bg-gray-700 text-white px-6 py-3 rounded-lg hover:bg-gray-800 transition-colors font-medium"
              >
                Get in Touch
              </a>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;